import React, { useState } from 'react'
import { Box, BoxProps, Image, Text } from '@fundamentals'
import WarningIcon from '@mui/icons-material/Warning'
import { ImageWarning } from '@core/entities/ImageWarning'
import type { Proof } from '@core/entities/template/TemplateQuestion'
import { styled } from '@mui/material/styles'
import { Palette } from '@core/theme/color-palette-siteassist'
import ImageModal from '@common/modal/ImageModal'
import { localizedStrings } from '@core/strings'
import { PdfModal } from '@common/PdfModal'
import { VideoModal } from '@common/VideoModal'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import { useModal } from '@common/GlobalModal'
import { AppraisalOverlay } from '@elements/Questions/AppraisalOverlay'
import { useNavigationContext } from '@common/hooks'
import { useFormContext } from 'react-hook-form'
import multiDownload from 'multi-download'
import { AttachmentInputValue } from '@elements/Questions/AttachmentInput'
import useFeatureFlags from '@core/providers/useFeatureFlags'
import { SiteBoxDocumentViewerModal } from '@modules/siteBoxDocumentSidebar'
import { FileTypeIcon } from '@modules/siteBox/components/FileTypeIcon'
import { computeFileFormat } from '@core/entities/SiteBoxDocument'

type AttachmentThumbnailProps = BoxProps & {
  proof: Proof
  warnings?: ImageWarning[]
  showWarningIcon?: boolean
  dataTest?: string
  size: 'small' | 'large'
  thumbnailFitToContent?: boolean
  index: number
  onAnnotate?: (blob: Blob, url: string) => void
  allAttachments?: Array<AttachmentInputValue>
  readOnly?: boolean
  projectId?: number
  preventDialogClose?: boolean
  capturedByName?: string
}

export const ATTACHMENT_THUMBNAIL_WIDTH = { small: 150, large: 300 } as const
const ATTACHMENT_THUMBNAIL_HEIGHT = { small: 100, large: 200 } as const

const StyledAttachmentPreview = styled(Box)<{
  size: 'small' | 'large'
  thumbnailFitToContent?: boolean
}>`
  overflow: hidden;
  background-color: black;
  border-radius: 4px;
  justify-content: center;
  align-items: center;
  position: relative;
  cursor: pointer;

  ${({ size, thumbnailFitToContent }) => `
    height: ${ATTACHMENT_THUMBNAIL_HEIGHT[size]}px;
    width: ${
      thumbnailFitToContent ? 'auto' : `${ATTACHMENT_THUMBNAIL_WIDTH[size]}px`
    };
  `}
  & img {
    height: 100%;
    object-fit: scale-down;
  }
`

const formatDuration = (seconds: number): string => {
  if (!isFinite(seconds)) return ''
  const minutes = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${minutes}:${secs.toString().padStart(2, '0')}`
}

export const AttachmentThumbnail: React.FC<AttachmentThumbnailProps> = ({
  proof,
  warnings,
  showWarningIcon,
  dataTest,
  size,
  index,
  thumbnailFitToContent,
  onAnnotate,
  readOnly,
  projectId,
  allAttachments,
  preventDialogClose,
  capturedByName,
}) => {
  const formContext = useFormContext()
  const { organisationId } = useNavigationContext()
  const { showModal } = useModal()
  const [videoDuration, setVideoDuration] = useState<number | null>(null)

  const { flags } = useFeatureFlags(['web_site_box_rams_appraisals'])

  const previewUrl = proof.uri || proof.getUrl || proof.downloadUrl
  const shouldShowWarningIcon =
    showWarningIcon ?? ImageWarning.showProofWarningIcon(warnings, proof.key)
  const placeholderFormat =
    proof.siteBoxDocument?.currentVersion?.format ??
    computeFileFormat(proof.type, proof.name) ??
    undefined

  const handleDownloadAll = () => {
    const downloadUrls = allAttachments
      .map((attachment) => attachment.downloadUrl)
      .filter((url): url is string => !!url)

    if (downloadUrls.length > 0) {
      multiDownload(downloadUrls)
    }
  }

  const openAttachmentPreview = (event: React.MouseEvent) => {
    event.stopPropagation()

    if (proof.type?.includes('video')) {
      showModal(
        <VideoModal
          title={localizedStrings.video}
          previewUrl={previewUrl}
          downloadUrl={proof.downloadUrl}
        />,
      )
      return
    }

    if (proof.type?.includes('image')) {
      const imageAttachments = allAttachments?.filter((p) =>
        p.type?.includes('image'),
      )
      showModal(
        <ImageModal
          title={localizedStrings.image}
          previewUrl={previewUrl}
          images={imageAttachments}
          s3Key={proof.key}
          canDownload
          objectDetections={warnings}
          preventDialogClose={preventDialogClose}
          capturedByName={capturedByName}
          onDownloadAll={handleDownloadAll}
        />,
      )
      return
    }

    if (proof.type?.includes('pdf')) {
      const documentAttachments =
        allAttachments?.filter((p) => p.type?.includes('pdf')) ?? []
      const hasAnySiteBoxDocInList = documentAttachments.some(
        (d) => d.siteBoxDocument,
      )
      const shouldUseSiteBoxViewer =
        flags.web_site_box_rams_appraisals &&
        (!formContext || readOnly) &&
        (proof.siteBoxDocument || hasAnySiteBoxDocInList)

      if (shouldUseSiteBoxViewer) {
        const proofIdentity = (p: Proof) =>
          p.siteBoxDocument?.id ?? p.id ?? p.key ?? p.uri

        return showModal(
          <SiteBoxDocumentViewerModal
            organisationId={organisationId}
            projectId={projectId}
            documents={documentAttachments.map((p) => {
              return {
                id: proofIdentity(p) ?? '',
                name: p.siteBoxDocument?.name ?? p.name ?? '',
                siteBoxDocument: p.siteBoxDocument,
                documentUrl: p.uri || p.getUrl || p.downloadUrl,
              }
            })}
            initialItemId={proofIdentity(proof)}
            readOnly
          />,
        )
      }

      showModal(
        <PdfModal
          readOnly={readOnly}
          title={localizedStrings.pdf}
          previewUrl={previewUrl}
          documents={documentAttachments}
          onAnnotate={onAnnotate}
          canDownload
          onDownloadAll={handleDownloadAll}
        />,
      )
    }
  }

  const renderThumbnail = () => {
    if (proof.type?.includes('video')) {
      return (
        <Box
          onClick={openAttachmentPreview}
          data-test={dataTest}
          sx={{
            position: 'relative',
            height: '100%',
            width: '100%',
            '&:hover .video-play-overlay': { opacity: 1 },
          }}
        >
          <video
            src={previewUrl}
            muted
            preload='metadata'
            onLoadedMetadata={(e) => setVideoDuration(e.currentTarget.duration)}
            style={{
              height: '100%',
              width: '100%',
              objectFit: 'cover',
              pointerEvents: 'none',
            }}
          />
          <Box
            className='video-play-overlay'
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0,
              transition: 'opacity 0.15s ease-in-out',
              backgroundColor: 'rgba(0, 0, 0, 0.25)',
            }}
          >
            <PlayCircleOutlineIcon sx={{ color: 'white', fontSize: 48 }} />
          </Box>
          {videoDuration != null && isFinite(videoDuration) && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 4,
                right: 4,
                px: 0.5,
                borderRadius: 0.5,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
              }}
            >
              <Text sx={{ color: 'white', fontSize: 12, lineHeight: 1.5 }}>
                {formatDuration(videoDuration)}
              </Text>
            </Box>
          )}
        </Box>
      )
    }

    if (proof.type?.includes('image')) {
      return (
        <Box onClick={openAttachmentPreview} height='100%' data-test={dataTest}>
          <Image
            src={previewUrl}
            sx={{
              pointerEvents: 'none',
              objectFit: 'cover',
              height: '100%',
              width: '100%',
            }}
          />
          {shouldShowWarningIcon && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                borderRadius: '50%',
                pt: 0.5,
                pr: 0.5,
              }}
            >
              <WarningIcon sx={{ color: '#FFA000', fontSize: 24 }} />
            </Box>
          )}
        </Box>
      )
    }

    if (proof.type?.includes('pdf')) {
      return (
        <Box onClick={openAttachmentPreview} height='100%' data-test={dataTest}>
          <AppraisalOverlay proof={proof}>
            <embed
              src={previewUrl}
              style={{ pointerEvents: 'none' }}
              type='application/pdf'
              height='100%'
              width='100%'
            />
          </AppraisalOverlay>
        </Box>
      )
    }

    // Non-previewable types (Office/CAD/video): a format-specific placeholder
    // tile instead of an empty thumbnail.
    return (
      <Box
        height='100%'
        width='100%'
        display='flex'
        alignItems='center'
        justifyContent='center'
        data-test={dataTest}
        sx={{ cursor: 'default' }}
      >
        <FileTypeIcon
          format={placeholderFormat}
          sx={{ fontSize: 48, color: Palette.Grey[700] }}
        />
      </Box>
    )
  }

  return (
    <StyledAttachmentPreview
      size={size}
      thumbnailFitToContent={thumbnailFitToContent}
      sx={{ backgroundColor: Palette.Grey[400] }}
      data-test={`upload-thumbnail-${index}`}
    >
      {renderThumbnail()}
    </StyledAttachmentPreview>
  )
}
